import {
  ArrowDownTrayIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { toPng } from "html-to-image";
import dynamic from "next/dynamic";
import QRCodeStyling from "qr-code-styling";
import { Fragment, useMemo, useRef } from "react";
import { Trans } from "react-i18next";
import useSWR from "swr";
import Button from "./Button";
import ModalComponent, { useModal } from "./Modal";
import QRCodeManager from "./QRCodeManager";

const QRRender = dynamic(() => import("@/components/QRRender"), {
  ssr: false,
});

type Props = {
  url: string;
  withEdit?: boolean;
  withQREdit?: boolean;
  disableDownload?: boolean;
  linkId?: any;
  onChoose?(): any;
  qrOnly?: boolean;
  id?: string;
  downloadOnClick?: boolean;
};

const qrcodeBaseWidth = 400;

export const useDownloadQR = () => {
  return {
    downloadQR: async (id) => {
      const qrCanvas: any = document.querySelector(`#${id || "qr-result"}`);

      if (!qrCanvas) {
        return;
      }

      const dataUrl = await toPng(qrCanvas, {
        cacheBust: true,
        pixelRatio: 2,
      });

      const link = document.createElement("a");
      link.download = "qr-" + Math.round(new Date().getTime() / 1000) + ".png";
      link.href = dataUrl;
      link.click();
      link.remove();
    },
  };
};

const QRCodeShare = ({
  url,
  linkId,
  withQREdit,
  qrOnly = false,
  id,
  downloadOnClick,
}: Props) => {
  const changeModal = useModal();
  const qrRef = useRef<QRCodeStyling>(null);
  const qrURL = useMemo(() => {
    const _url = new URL(url);
    _url.searchParams.set("s", "qr");
    return _url.toString();
  }, [url]);

  const qrData = useSWR(() => (linkId ? `/links/${linkId}/qrcode` : null));
  const isLoading = !qrData.data && qrData.isValidating;

  const downloadQrCode = async () => {
    if (isLoading) return;

    const _url = new URL(url);
    const [, slug] = _url.pathname.split("/");

    qrRef.current?.download({
      name: "qr-" + slug,
      extension: "png",
    });
  };

  const data = useMemo(() => {
    return qrData.data?.data || {};
  }, [qrData]);

  return (
    <>
      <div className="flex md:flex-row flex-col justify-center mt-2">
        <div className="flex md:flex flex-col justify-center items-center">
          <div className="flex justify-center items-center">
            <div className="relative !max-w-[200px]">
              {downloadOnClick ? (
                <button className="absolute inset-0" onClick={downloadQrCode} />
              ) : (
                <Fragment />
              )}
              <QRRender
                url={qrURL}
                id={id}
                qrRef={(r) => (qrRef.current = r)}
                data={data}
                isLoading={isLoading}
              />
            </div>
          </div>
          {!qrOnly && (
            <>
              <h2 className="mt-4 mb-2 text-gray-500 dark:text-gray-200 text-sm">
                {url}
              </h2>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  className="mt-2 text-sm"
                  size="sm"
                  disabled={isLoading}
                  onClick={downloadQrCode}
                >
                  <ArrowDownTrayIcon className="size-4" />
                  Download
                </Button>
                {withQREdit && (
                  <Button
                    variant="secondary"
                    className="mt-2 text-sm"
                    size="sm"
                    disabled={isLoading}
                    onClick={() => {
                      if (isLoading) return;
                      changeModal.setOpen(true);
                    }}
                  >
                    <PencilSquareIcon className="size-4" />
                    <Trans i18nKey="dashboard:change">Change</Trans>
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <ModalComponent {...changeModal} disabled className="!max-w-[590px]">
        <div className="flex">
          <h1 className="flex-1 font-montserrat font-semibold text-xl">
            <Trans i18nKey="dashboard:change_qrcode_style">
              Change QR Code Style
            </Trans>
          </h1>
          <button onClick={changeModal.close}>
            <XMarkIcon className="w-7" />
          </button>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 mt-3 p-2 rounded-md w-full h-[350px] overflow-x-hidden overflow-y-auto sid-scrollbar">
          <QRCodeManager
            chooseOnly
            onChoose={() => {
              qrData.mutate();
            }}
            linkId={linkId}
          />
        </div>
      </ModalComponent>
    </>
  );
};

export default QRCodeShare;
